import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Mail, Lock, ArrowRight, TrendingUp, Shield, BarChart3, Truck, ScanLine, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import api from '../lib/api';

const LoginPage = () => {
  const [activeTab, setActiveTab] = useState('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [orgSettings, setOrgSettings] = useState(null);
  const [twofaState, setTwofaState] = useState(null); // { action, challenge_token, enrol_token, email }
  const [twofaCode, setTwofaCode] = useState('');
  const [enrolStep, setEnrolStep] = useState(null); // { qr_code, secret, backup_codes }
  const { login, verifyTwoFactor } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/system-settings/public').then(res => setOrgSettings(res.data)).catch(() => {});
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result?.twofa_required) {
        setTwofaState(result);
        setLoading(false);
        return;
      }
      navigate('/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') setError(detail);
      else if (Array.isArray(detail)) setError(detail.map(e => e.msg || JSON.stringify(e)).join(' '));
      else setError('Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2fa = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const r = await verifyTwoFactor(twofaState.challenge_token, twofaCode);
      if (r?.remaining_backup_codes != null && r.backup_code_used) {
        // optional toast
      }
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid code. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStartEnrol = async () => {
    setError(''); setLoading(true);
    try {
      // Use the temporary enrol token to call setup
      const res = await api.post('/auth/2fa/setup', {}, {
        headers: { Authorization: `Bearer ${twofaState.enrol_token}` },
      });
      setEnrolStep({ ...res.data, code: '', backup_codes: null });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start 2FA setup.');
    } finally { setLoading(false); }
  };

  const handleConfirmEnrol = async () => {
    setError(''); setLoading(true);
    try {
      const res = await api.post('/auth/2fa/enable', { code: enrolStep.code }, {
        headers: { Authorization: `Bearer ${twofaState.enrol_token}` },
      });
      setEnrolStep(s => ({ ...s, backup_codes: res.data.backup_codes }));
    } catch (err) {
      setError(err.response?.data?.detail || 'Wrong code, please try again.');
    } finally { setLoading(false); }
  };

  const handleFinishEnrol = async () => {
    // Backup codes shown — now log in afresh with the same password (user has just enabled 2FA)
    setError('');
    setEnrolStep(null);
    setTwofaState(null);
    setTwofaCode('');
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result?.twofa_required) {
        setTwofaState(result);
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError('Please log in again to complete sign-in.');
    } finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/register', { email, password, name, role: 'teller' });
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') setError(detail);
      else setError('Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const orgName = orgSettings?.organization_name || "N K O S I ' S";
  const orgTagline = orgSettings?.organization_tagline || '';
  const branchCount = orgSettings?.branch_count ?? '--';
  const provinceCount = orgSettings?.province_count ?? '--';
  const companyLogo = (orgSettings?.company_logo && orgSettings.company_logo.length > 10) ? orgSettings.company_logo : null;

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-1/2 login-left-panel flex-col justify-between p-12">
        <div>
          <div>
            <div>
              <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '11px', letterSpacing: '0.25em', fontWeight: 700 }} className="text-white">
                N K O S I ' S
              </p>
              <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '9px', letterSpacing: '0.18em', fontWeight: 600 }} className="text-[#C5A059] mt-1 mb-3">
                CORE BANKING SYSTEM
              </p>
            </div>
            {companyLogo && (
              <img src={companyLogo} alt="Company Logo" className="h-10 w-auto max-w-[120px] object-contain mb-6" data-testid="login-company-logo" />
            )}
            {!companyLogo && <div className="mb-6" />}
          </div>
        </div>
        
        <div className="space-y-5">
          <h1 style={{ fontFamily: 'Poppins, sans-serif' }} className="text-5xl font-bold text-white leading-tight" data-testid="login-org-name">
            {orgName}
          </h1>
          
          <p className="text-gray-400 text-lg max-w-md leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
            {orgTagline || 'Institutional-grade core banking platform powered by SARB compliance, regulatory reporting, and real-time interest rate integration.'}
          </p>
          
          {/* Stats */}
          <div className="flex gap-6">
            <div className="bg-white/5 border border-white/10 p-4" data-testid="stat-branches">
              <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>{branchCount}</p>
              <p className="text-xs uppercase tracking-wider text-gray-500 mt-1">BRANCHES</p>
            </div>
            <div className="bg-white/5 border border-white/10 p-4" data-testid="stat-provinces">
              <p className="text-3xl font-bold text-[#C5A059]" style={{ fontFamily: 'Poppins, sans-serif' }}>{provinceCount}</p>
              <p className="text-xs uppercase tracking-wider text-gray-500 mt-1">PROVINCES</p>
            </div>
            <div className="bg-white/5 border border-white/10 p-4" data-testid="stat-sarb">
              <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>SARB</p>
              <p className="text-xs uppercase tracking-wider text-gray-500 mt-1">COMPLIANT</p>
            </div>
          </div>
          
          {/* Features */}
          <div className="space-y-3 pt-2">
            <p className="text-gray-300 text-sm font-medium" style={{ fontFamily: 'Inter, sans-serif' }}>
              Institutional-grade core banking platform powered by SARB compliance, regulatory reporting, and real-time interest rate integration.
            </p>
            <div className="flex items-center gap-3 text-gray-400">
              <ScanLine className="w-5 h-5 text-[#C5A059] flex-shrink-0" />
              <span className="text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>AI-powered document scanning with auto-populated client fields</span>
            </div>
            <div className="flex items-center gap-3 text-gray-400">
              <TrendingUp className="w-5 h-5 text-[#C5A059] flex-shrink-0" />
              <span className="text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>ZARONIA & Repo rate integration with loan pricing</span>
            </div>
            <div className="flex items-center gap-3 text-gray-400">
              <Shield className="w-5 h-5 text-[#C5A059] flex-shrink-0" />
              <span className="text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>Credit scoring & lending decision automation</span>
            </div>
            <div className="flex items-center gap-3 text-gray-400">
              <BarChart3 className="w-5 h-5 text-[#C5A059] flex-shrink-0" />
              <span className="text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>BA, DI, MFI & CFI regulatory returns generation</span>
            </div>
            <div className="flex items-center gap-3 text-gray-400">
              <Truck className="w-5 h-5 text-[#C5A059] flex-shrink-0" />
              <span className="text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>Bulk client & transaction data migration tools</span>
            </div>
          </div>
        </div>
        
        <p className="text-gray-600 text-sm pt-8" style={{ fontFamily: 'Inter, sans-serif' }}>
          &copy; 2026 Beaconwood Partners (Pty) Ltd
        </p>
      </div>
      
      {/* Right Panel - Login/Register Form */}
      <div className="w-full lg:w-1/2 bg-white flex items-center justify-center p-8 lg:p-12">
        <div className="w-full max-w-md">
          <div className="mb-6 text-center">
            <div className="flex items-center justify-center gap-2 mb-1">
              {companyLogo && <img src={companyLogo} alt="" className="h-6 w-auto max-w-[60px] object-contain lg:hidden" />}
              <p className="lg:hidden text-xs font-bold tracking-[0.25em] text-[#1f1f1f]" style={{ fontFamily: 'Poppins, sans-serif' }}>N K O S I ' S</p>
            </div>
            <h2 style={{ fontFamily: 'Poppins, sans-serif' }} className="text-3xl font-bold text-[#1f1f1f]">
              Welcome
            </h2>
            <p className="text-gray-500 mt-2" style={{ fontFamily: 'Inter, sans-serif' }}>
              Sign in to access the Core Banking Platform
            </p>
          </div>

          {/* Tabs */}
          <div className="flex border border-gray-200 rounded-lg mb-6 overflow-hidden" data-testid="auth-tabs">
            <button
              onClick={() => { setActiveTab('signin'); setError(''); }}
              className={`flex-1 py-2.5 text-sm font-semibold transition-colors ${
                activeTab === 'signin'
                  ? 'bg-[#F0F4F8] text-[#1f1f1f] border-b-2 border-[#1f1f1f]'
                  : 'bg-white text-gray-400 hover:text-gray-600'
              }`}
              style={{ fontFamily: 'Poppins, sans-serif' }}
              data-testid="signin-tab"
            >
              Sign In
            </button>
            <button
              onClick={() => { setActiveTab('register'); setError(''); }}
              className={`flex-1 py-2.5 text-sm font-semibold transition-colors ${
                activeTab === 'register'
                  ? 'bg-[#F0F4F8] text-[#1f1f1f] border-b-2 border-[#1f1f1f]'
                  : 'bg-white text-gray-400 hover:text-gray-600'
              }`}
              style={{ fontFamily: 'Poppins, sans-serif' }}
              data-testid="register-tab"
            >
              Register
            </button>
          </div>
          
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm mb-4" data-testid="login-error">
              {error}
            </div>
          )}

          {twofaState && twofaState.twofa_action === 'verify' && (
            <form onSubmit={handleVerify2fa} className="space-y-5" data-testid="twofa-verify-form">
              <div className="bg-amber-50 border border-amber-200 px-4 py-3 text-xs text-amber-800 rounded">
                Two-factor authentication is enabled. Enter the 6-digit code from your authenticator app (or a backup code).
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">6-Digit Code</Label>
                <Input
                  value={twofaCode}
                  onChange={(e) => setTwofaCode(e.target.value)}
                  placeholder="123456"
                  maxLength={9}
                  autoFocus
                  className="py-6 font-mono text-center text-lg tracking-widest"
                  data-testid="twofa-code-input"
                />
              </div>
              <Button type="submit" disabled={loading} className="w-full py-6 bg-[#1f1f1f] hover:bg-[#333333] text-white" data-testid="twofa-verify-button">
                {loading ? 'Verifying...' : 'Verify & Sign In'}
              </Button>
              <button type="button" onClick={() => { setTwofaState(null); setTwofaCode(''); setError(''); }} className="text-xs text-gray-500 hover:text-gray-700 w-full text-center" data-testid="twofa-back-link">
                ← Back to sign in
              </button>
            </form>
          )}

          {twofaState && twofaState.twofa_action === 'enrol' && !enrolStep && (
            <div className="space-y-5" data-testid="twofa-enrol-prompt">
              <div className="bg-amber-50 border border-amber-200 px-4 py-3 text-xs text-amber-800 rounded">
                Your administrator requires Two-Factor Authentication. Set it up now using an authenticator app (Google Authenticator, Authy, Microsoft Authenticator, 1Password, etc.).
              </div>
              <Button onClick={handleStartEnrol} disabled={loading} className="w-full py-6 bg-[#1f1f1f] hover:bg-[#333333] text-white" data-testid="twofa-start-enrol">
                {loading ? 'Loading...' : 'Set Up Two-Factor Authentication'}
              </Button>
            </div>
          )}

          {twofaState && enrolStep && !enrolStep.backup_codes && (
            <div className="space-y-4" data-testid="twofa-enrol-qr">
              <p className="text-xs text-gray-600">Scan this QR code with your authenticator app:</p>
              <div className="flex justify-center bg-white border border-gray-200 p-4">
                <img src={enrolStep.qr_code} alt="2FA QR Code" className="w-48 h-48" data-testid="twofa-qr-image" />
              </div>
              <details className="text-xs text-gray-500">
                <summary className="cursor-pointer">Can't scan? Show secret key</summary>
                <p className="mt-2 font-mono bg-gray-50 p-2 break-all">{enrolStep.secret}</p>
              </details>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Enter the 6-digit code from your app</Label>
                <Input
                  value={enrolStep.code}
                  onChange={(e) => setEnrolStep(s => ({ ...s, code: e.target.value }))}
                  placeholder="123456"
                  maxLength={6}
                  autoFocus
                  className="py-6 font-mono text-center text-lg tracking-widest"
                  data-testid="twofa-enrol-code-input"
                />
              </div>
              <Button onClick={handleConfirmEnrol} disabled={loading || !enrolStep.code} className="w-full py-6 bg-[#1f1f1f] hover:bg-[#333333] text-white" data-testid="twofa-confirm-enrol">
                {loading ? 'Verifying...' : 'Confirm & Enable'}
              </Button>
            </div>
          )}

          {twofaState && enrolStep && enrolStep.backup_codes && (
            <div className="space-y-4" data-testid="twofa-backup-codes">
              <div className="bg-emerald-50 border border-emerald-200 px-4 py-3 text-xs text-emerald-800 rounded">
                ✓ Two-factor authentication enabled. <strong>Save these backup codes.</strong> Each one can be used once if you lose your authenticator device. They will not be shown again.
              </div>
              <div className="grid grid-cols-2 gap-2 bg-gray-50 border border-gray-200 p-4 font-mono text-sm">
                {enrolStep.backup_codes.map((c, i) => (
                  <div key={i} className="px-2 py-1 bg-white border border-gray-200 text-center" data-testid={`backup-code-${i}`}>{c}</div>
                ))}
              </div>
              <Button onClick={handleFinishEnrol} disabled={loading} className="w-full py-6 bg-[#1f1f1f] hover:bg-[#333333] text-white" data-testid="twofa-finish-enrol">
                I have saved them — Continue
              </Button>
            </div>
          )}

          {!twofaState && activeTab === 'signin' ? (
            <form onSubmit={handleLogin} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-gray-500">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="pl-12 py-6 border-gray-200 focus:border-[#1f1f1f] focus:ring-[#1f1f1f]"
                    required
                    data-testid="login-email-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-gray-500">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="pl-12 py-6 border-gray-200 focus:border-[#1f1f1f] focus:ring-[#1f1f1f]"
                    required
                    data-testid="login-password-input"
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  className="text-sm text-[#C5A059] hover:text-[#B89249] font-medium"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </button>
              </div>
              
              <Button
                type="submit"
                disabled={loading}
                className="w-full py-6 bg-[#1f1f1f] hover:bg-[#333333] text-white font-medium flex items-center justify-center gap-2"
                data-testid="login-submit-button"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>Sign In <ArrowRight className="w-4 h-4" /></>
                )}
              </Button>
            </form>
          ) : !twofaState && (
            <form onSubmit={handleRegister} className="space-y-5">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="John Doe"
                    className="pl-12 py-6 border-gray-200 focus:border-[#1f1f1f] focus:ring-[#1f1f1f]"
                    required
                    data-testid="register-name-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="pl-12 py-6 border-gray-200 focus:border-[#1f1f1f] focus:ring-[#1f1f1f]"
                    required
                    data-testid="register-email-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Create a password"
                    className="pl-12 py-6 border-gray-200 focus:border-[#1f1f1f] focus:ring-[#1f1f1f]"
                    required
                    data-testid="register-password-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                    className="pl-12 py-6 border-gray-200 focus:border-[#1f1f1f] focus:ring-[#1f1f1f]"
                    required
                    data-testid="register-confirm-password-input"
                  />
                </div>
              </div>
              
              <Button
                type="submit"
                disabled={loading}
                className="w-full py-6 bg-[#1f1f1f] hover:bg-[#333333] text-white font-medium flex items-center justify-center gap-2"
                data-testid="register-submit-button"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>Create Account <ArrowRight className="w-4 h-4" /></>
                )}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
